import { useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { useSettings } from "../../../contexts/settings-context";
import { CompactModuleFilter } from "../../utils/RelatedEntries/CompactModuleFilter";
import { useRelatedCollection } from "../../utils/RelatedEntries/useRelatedCollection";
import { RelatedEntries } from "../../utils/RelatedEntries/RelatedEntries";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../utils/PMTableView/RightDrawer";
import {
  mergeScopedFilters,
  useScopeFiltersSync,
  computeHasMore,
  embeddedFetchKey,
  scopeFiltersKey,
} from "../../utils/PMTableView/embedded-table-utils";
import { getOrdersAsync } from "../../../slices/order";
import { OrderStatus } from "./utils";
import { CustomerTitle, ProjectTitle } from "../../Utils";

export const OrdersStacklist = (props) => {
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  return <OrdersStacklistContent key={JSON.stringify([companyId, props.module, props.filters])} {...props} />;
};

const OrdersStacklistContent = (props) => {
  const { filters: scopeFilters = [], module: moduleName = "embedded" } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { priceFormatter } = useSettings();

  const [itemState, loadItems] = useRelatedCollection(getOrdersAsync, JSON.stringify(scopeFilters));
  const { isLoading, totalCount } = itemState;

  const loadMoreInFlightRef = useRef(false);
  const [drawerItem, setDrawerItem] = useState(null);

  const [controller, setController] = useState(() => ({
    filters: [...(scopeFilters || [])],
    page: 1,
    per_page: props.per_page ? parseInt(props.per_page, 10) : 25,
    query: "",
    sort: "desc",
    sortBy: "created_at",
    view: "all",
  }));

  useScopeFiltersSync(scopeFilters, setController);

  const handleFiltersChange = (filters) => {
    const userFilters = [];
    if (filters.statuses?.length > 0) {
      userFilters.push({
        name: "status",
        value: JSON.stringify(filters.statuses),
      });
    }
    setController((c) => ({
      ...c,
      page: 1,
      query: filters.query || "",
      filters: mergeScopedFilters(scopeFilters, userFilters),
    }));
  };

  const getInitialFilters = () => {
    const initial = { query: controller.query || "", statuses: [] };
    controller.filters.forEach((filter) => {
      if ((scopeFilters || []).some((sf) => sf.name === filter.name)) return;
      if (filter.name !== "status") return;
      try {
        initial.statuses = JSON.parse(filter.value);
      } catch {
        initial.statuses = [filter.value];
      }
    });
    return initial;
  };

  const handleLoadMore = useCallback(() => {
    const loaded = itemState?.data?.length || 0;
    const total = totalCount || 0;
    if (loadMoreInFlightRef.current || isLoading || loaded >= total) return;

    const nextPage = (parseInt(controller.page, 10) || 1) + 1;
    loadMoreInFlightRef.current = true;
    loadItems({ ...controller, page: nextPage, append: true }).then((loaded) => { if (loaded) setController((c) => ({ ...c, page: nextPage })); }).finally(
      () => {
        loadMoreInFlightRef.current = false;
      }
    );
  }, [
    controller,
    loadItems,
    isLoading,
    itemState?.data?.length,
    totalCount,
  ]);

  const debouncedFetch = useMemo(
    () => debounce((ctrl) => loadItems(ctrl), 250),
    [loadItems]
  );

  const filtersKey = scopeFiltersKey(controller.filters);
  const fetchKey = useMemo(
    () => embeddedFetchKey(controller),
    [
      filtersKey,
      controller.query,
      controller.sort,
      controller.sortBy,
      controller.per_page,
      controller.view,
    ]
  );

  useEffect(() => {
    setController((c) => (c.page === 1 ? c : { ...c, page: 1 }));
    debouncedFetch({ ...controller, page: 1 });
    return () => debouncedFetch.cancel();
  }, [fetchKey, debouncedFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  const dataLength = itemState?.data?.length || 0;
  const hasMore = computeHasMore({ isLoading, dataLength, totalCount });

  return (
    <div className="min-w-0 space-y-4">
      <CompactModuleFilter
        disabled={isLoading}
        onFiltersChange={handleFiltersChange}
        initialFilters={getInitialFilters()}
        searchPlaceholder={t("Search orders...")}
        filterTitle={t("Filter Orders")}
        acceptedStatuses={["draft", "ongoing", "finished", "cancelled"]}
      />

      <div className="min-w-0">
        <RelatedEntries kind="orders" context={moduleName} data={itemState.data} isLoading={isLoading} error={itemState.error} totalCount={totalCount}
          onRetry={() => { setController((value) => ({ ...value, page: 1 })); loadItems({ ...controller, page: 1 }); }}
          onRowClick={setDrawerItem} renderStatus={(item) => <OrderStatus order={item} />} renderActions={(item) => <span className="text-xs font-semibold text-slate-600">{t("order.total")}: {priceFormatter.format(item.total_amount || 0)}</span>} hasMore={hasMore} onLoadMore={handleLoadMore}
        />
      </div>

      <RightDrawer
        open={!!drawerItem}
        onClose={() => setDrawerItem(null)}
        eyebrow={drawerItem ? `${t("order.number")} ${drawerItem.order_number || ""}` : ""}
        title={drawerItem?.displayTitle || t("Orders")}
        actions={[
          {
            label: t("Open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () => navigate(`/dashboard/orders/${drawerItem.id}`),
          },
        ]}
      >
        {drawerItem && (
          <DrawerSection>
            <DrawerProps
              items={[
                {
                  label: t("status"),
                  value: <OrderStatus order={drawerItem} />,
                },
                {
                  label: t("customer"),
                  value: <CustomerTitle customer={drawerItem?.customer} />,
                },
                {
                  label: t("project"),
                  value: drawerItem?.project ? (
                    <ProjectTitle project={drawerItem.project} />
                  ) : (
                    "—"
                  ),
                },
                {
                  label: t("order.total"),
                  value: priceFormatter.format(drawerItem?.total_amount || 0),
                },
              ]}
            />
          </DrawerSection>
        )}
      </RightDrawer>
    </div>
  );
};

OrdersStacklist.defaultProps = {
  filters: [],
  sort: "desc",
  sortBy: "created_at",
};
